// How to play: start and end the tutorial. The practice park lives only in this page; when it ends
// (finished or skipped) we tell our server once, so it is not offered again, and How to play in the
// menu replays it any time.
import { ParkSession } from '../game/session';
import { api } from '../lib/api';
import { useUi } from '../store/ui';
import type { LocalPark } from './local';

/** Offered once per visit at most, even if telling the server failed. */
let offered = false;

export function offerPractice(): void {
  if (offered) return;
  offered = true;
  startPractice();
}

/**
 * The practice park's room, script and bots load only when it is asked for (they are most of a
 * worker): the menu's shell stays small.
 */
export function startPractice(): void {
  void Promise.all([import('./local'), import('./director')]).then(([l, d]) => runPractice(l.LocalPark, d.Director));
}

function runPractice(Local: typeof LocalPark, Director: typeof import('./director').Director): void {
  const ui = useUi.getState();
  ui.park?.leave();
  let local: LocalPark | null = null;
  let director: InstanceType<typeof Director> | null = null;
  const session = new ParkSession(
    { code: 'PRACTICE', roomId: 'PRACTICE', workerUrl: '', ticket: '' },
    () => {
      const u = useUi.getState();
      if (u.park === session) u.set({ park: null, practice: null, screen: 'menu' });
    },
    {
      net: (o) => {
        local = new Local(o, {
          steer: (s, hands) => director?.steer(s, hands) ?? true,
          created: (s) => director?.created(s),
          onTick: (room) => director?.tick(room),
          name: ui.me?.name ?? 'You',
        });
        return local;
      },
    },
  );
  director = new Director(
    session,
    () => (local as unknown as LocalPark).room,
    // through the session, so each action carries its own id like a phone's
    (intent) => void session.act(intent as never),
    () => markDone(),
  );
  ui.set({ park: session, practice: director, screen: 'park', notice: null, joining: false, entering: null });
  // test mode only: let the browser tests look inside
  if (ui.config?.mode === 'test') (window as unknown as { __practice?: unknown }).__practice = { session, director };
}

/** Finished or skipped: never offered again (the server keeps it on the player's profile). */
export function markDone(): void {
  api.tutorialDone().catch(() => {});
}

/** Leave the practice park for the menu (Skip, or the last card's Back to menu). */
export function endPractice(skipped: boolean): void {
  const ui = useUi.getState();
  if (skipped) markDone();
  ui.park?.leave();
  ui.set({ park: null, practice: null, screen: 'menu' });
}
